import CardCommon from '@op/components/CardCommon';
import { useMemeTrend } from '@opm/pages/Memes/hooks';
import React, { useCallback, useMemo, useRef } from 'react';
import EChartCommon from '@op/components/EChartCommon';
import { getXAxisAxisLabel } from '@op/utils/chart';
import { fillNullWithZero } from '@op/pages/Overview/RealtimeOpinions/utils';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@op/components/ChartCommon/formatter';
import { echartsContent } from '@op/utils';
import useIntls from '@opm/hooks/useIntls';
import { gotoDownload } from '@opm/pages/Download';
import BubbleCommon from '@op/components/BubbleCommon';
import { MEME_TREND_TIP } from '@opm/pages/Memes/MemesTable/constants';

interface IMemeTrendProps {
  filter: any;
  firstAvailableTime?: string;
  /** Compact mode for drawer panel: no filter, no download, smaller height */
  compact?: boolean;
  /** Override data externally (e.g. mock data) — skips internal useMemeTrend hook */
  externalData?: any;
  externalLoading?: boolean;
  /** Currently selected date on the bar chart */
  selectedDate?: string;
  /** Callback when a bar is clicked (toggle selection) */
  onDateSelect?: (date: string | undefined) => void;
}

export default function MemeTrend(props: IMemeTrendProps) {
  const { filter, compact = false, externalData, externalLoading, selectedDate, onDateSelect } = props;

  const { intl } = useIntls();

  const useInternalData = externalData === undefined;
  const { loading: hookLoading, data: hookData } = useMemeTrend({
    filter,
  });

  const loading = useInternalData ? hookLoading : externalLoading ?? false;

  const data = useMemo(() => {
    if (loading || externalLoading) {
      return undefined;
    }
    if (useInternalData) {
      return hookData;
    }
    return externalData;
  }, [loading, useInternalData, externalData, hookData]);

  const dealWithKeyValue = (key: string, valueKey?: 'label' | 'color') => {
    const keyMaps: Record<
      string,
      {
        label: string;
        color: string;
        type: 'bar' | 'line';
        yAxisIndex: number;
      }
    > = {
      views: {
        label: 'TikTok Views',
        color: '#5086F3',
        type: 'bar',
        yAxisIndex: 0,
      },
      publications: {
        label: 'Publications',
        color: '#69D19C',
        type: 'line',
        yAxisIndex: 1,
      },
    };
    const selected = keyMaps[key];
    if (valueKey) {
      return selected[valueKey];
    }
    return selected;
  };

  const options = useMemo(() => {
    if (!filter || !data) {
      return {};
    }
    const { series: seriesData, legend, xAxis, isEmpty } = data || {};
    if (isEmpty) return {};
    const selectedIndex = selectedDate && xAxis
      ? xAxis.indexOf(selectedDate)
      : -1;

    const series = (seriesData || []).map((val: any) => {
      const { name, data: valData } = val;
      const seriesInfo = dealWithKeyValue(name) as any;
      const item = { ...val };
      const filledData = fillNullWithZero(valData);

      // Highlight selected bar with full opacity, dim others
      const color = seriesInfo?.itemStyle?.color || val?.itemStyle?.color;
      const styledData = selectedIndex >= 0 && seriesInfo?.type === 'bar'
        ? filledData.map((v: any, idx: number) => {
          if (idx === selectedIndex) {
            return {
              value: v,
              itemStyle: {
                borderColor: '#000',
                borderWidth: 2,
                color,
                opacity: 1,
              },
              emphasis: {
                itemStyle: {
                  borderColor: '#000',
                  borderWidth: 2,
                  opacity: 1,
                },
              },
            };
          }
          return {
            value: v,
          };
        })
        : filledData;

      return {
        ...item,
        data: styledData,
        ...(seriesInfo || {}),
        ...(selectedIndex >= 0 && seriesInfo?.type === 'bar'
          ? { cursor: 'pointer' }
          : {}),
      };
    });

    const options = {
      legend: {
        data: legend,
        formatter: (params: string) => {
          const label = dealWithKeyValue(params, 'label') as string;
          return intl(label);
        },
      },
      xAxis: {
        data: xAxis,
        axisLabel: getXAxisAxisLabel(filter),
      },
      grid: {
        left: 10,
        right: 10,
        top: 20,
      },
      series,
      tooltip: {
        className: 'dbo-mentions-sentiment-box-tooltip-wrapper',
        formatter: (params: any) => {
          const leftTitle = listTimeTitleFunc(
            params[0]?.axisValue,
            filter?.date_type || 'hourly',
          );
          return formatChartParamsDate({
            title: leftTitle,
            params,
            isSort: true,
            listTitleFunc: (item: any) => {
              const label = dealWithKeyValue(
                item.seriesName,
                'label',
              ) as string;
              return intl({ id: label });
            },
            dealwithRecords: (records: any) => {
              records.push({
                text: `<span class="dbom-chart-common-formatter-tips">${intl({
                  id: 'Click the chart to view related videos',
                })}</span>`,
                value: '',
                originValue: 0,
                originText: 'Click the chart to view related videos',
                share: '',
              });
              return records;
            }
          });
        },
      },
      yAxis: [
        {
          type: 'value',
          axisLabel: {
            formatter: (value: any) => echartsContent(value),
            color: '#838697',
          },
        },
        {
          type: 'value',
          axisLabel: {
            formatter: (value: any) => echartsContent(value),
            color: '#838697',
          },
        },
      ],
    };

    return options;
  }, [data, intl, selectedDate]);

  function handleDownload() {
    if (compact) return;
    gotoDownload({
      key: 'MemeTrendDownload',
      fileName: `Meme Trend`,
      data: data?.tableRecord || [],
      filter,
    });
  }

  const chartHeight = compact ? 250 : 400;

  const handleClickRef = useRef<(date: string) => void>();
  handleClickRef.current = (clickedDate: string) => {
    if (!onDateSelect) return;
    onDateSelect(selectedDate === clickedDate ? undefined : clickedDate);
  };

  const handleChartReady = useCallback((chartInstance: any) => {
    if (!chartInstance) return;

    chartInstance.getZr().off('click');
    chartInstance.getZr().on('click', (params: any) => {
      const pointInPixel = [params.offsetX, params.offsetY];
      if (!chartInstance.containPixel('grid', pointInPixel)) return;

      const xIndex = Math.round(chartInstance.convertFromPixel({ seriesIndex: 0 }, pointInPixel)[0]);
      const xAxisData = chartInstance.getOption()?.xAxis?.[0]?.data;
      if (!xAxisData || xIndex < 0 || xIndex >= xAxisData.length) return;

      handleClickRef.current?.(xAxisData[xIndex]);
    });
  }, []);

  return (
    <CardCommon
      title={(
        <BubbleCommon
          tipsMap={MEME_TREND_TIP}
          showIcon={false}
          placement={'top'}
        >
          {compact ? intl('Stats based on Top 500 Video by views') : intl('Meme Trend')}
        </BubbleCommon>
      )}
      subTitle={
        compact
          ? ""
          : intl(
            'Calculated using TikTok data for new videos entering the all-time Top 500 by views.',
          )
      }
      className={compact ? 'dbom-meme-trend--compact' : 'dbom-meme-trend'}
      loading={loading}
      hasDownload={!compact}
      handleDownloadOnClick={() => handleDownload()}
      empty={data?.isEmpty}
      loadingHeight={chartHeight}
      emptyHeight={chartHeight}
      contentCard={compact}
    >
      <EChartCommon
        optionsProps={{ type: 'bar' }}
        options={options}
        height={chartHeight}
        onChartReady={handleChartReady}
      />
    </CardCommon>
  );
}
